import firebase from 'firebase/compat/app';
import { SetRequired } from 'type-fest';
import { firestore } from '../api/firebase/firebase';
import {
  createQuerySnapshot,
  getDocumentsAsArray,
  saveDocument,
  startCollectionListener,
} from '../api/firebase/firestore';

type WriteBatch = firebase.firestore.WriteBatch;

import { Contact, ContactStatus, ContactType } from '@wonderschool/common-base-types';
import { fetchEmailUsage } from '../api/firebase/users';
import { formatFullName, getDisplayNameOrFormatFullName } from '../helpers/utils';
// Type-only import (erased at compile time), so contactsAPI doesn't pull the
// reports barrel — and its React components — into its runtime module graph.
import type { QueryConditionType } from '../reports/types';
import { determineContactStatus } from './determineContactStatus';
import { INVITATION_TYPES } from './enums';

const paths = {
  contact: (organizationId, contactId) => `organizations/${organizationId}/contacts/${contactId}`,
  contacts: (organizationId) => `organizations/${organizationId}/contacts`,
};

export const contactsOnListen = ({ organizationId, conditions }, next, error, complete?: () => void) => {
  return startCollectionListener(
    {
      path: paths.contacts(organizationId),
      conditions,
      orderBy: [{ field: 'firstName', direction: 'asc' }],
    },
    next,
    error,
    complete
  );
};

export async function createContactForStudent(organization, location, student, contactData) {
  const contact = {
    ...contactData,
    displayName: formatFullName(contactData),
    defaultOrganization: organization.id,
    organizations: [organization.id],
    defaultLocation: location?.id || '',
    locations: location?.id ? [location.id] : [],
    student: {
      id: student.id ?? '',
      firstName: student.firstName ?? '',
      lastName: student.lastName ?? '',
      displayName: getDisplayNameOrFormatFullName(student),
      organization: student.organization ?? '',
    },
    studentIds: student?.id ? [student.id] : [],
    invitationType: contactData.invitationType ?? INVITATION_TYPES.parent,
    // Always persist an explicit status. Without this, a contact can be saved
    // with `status: undefined`, which downstream UI (ContactStatusLabel) renders
    // as a misleading "Disabled" badge and blocks re-inviting the contact.
    status: contactData.status ?? ContactStatus.new,
  };
  const contactDocRef = await saveContact(organization.id, contact);
  contact.id = contactDocRef.id;
  return { contact, contactDocRef };
}

export async function updateContactForStudent(organization, location, student, contactData) {
  const contact = {
    ...contactData,
    displayName: formatFullName(contactData),
  };
  const contactDocRef = await saveContact(organization.id, contact);
  return { contact, contactDocRef };
}

// Return the docref, since we need the id. Pass `batch` to fold this write
// into a caller-owned Firestore batch (e.g. so a contact + its student are
// committed atomically) instead of writing immediately.
export async function saveContact(
  organizationId,
  contact,
  firestoreOptions = { merge: true },
  batch: WriteBatch | null = null
) {
  if (!organizationId) throw new Error('Organization Id is required');
  if (!contact) throw new Error('Contact is required');
  const { userId, key, combinedFamily, ...rest } = contact;

  const data = {
    ...rest,
    ...(rest?.email ? { email: rest.email.toLowerCase() } : {}),
    defaultOrganization: organizationId,
    organizations: firestore.FieldValue.arrayUnion(organizationId),
  };
  return saveDocument({
    collectionPath: paths.contacts(organizationId),
    data,
    firestoreOptions,
    batch,
  });
}

type InvitableContact = SetRequired<Contact, 'email' | 'defaultOrganization' | 'status'>;
export function isInvitableContact(contact: Contact): contact is InvitableContact {
  return !!contact.email && !!contact.defaultOrganization && !!contact.status;
}

export const inviteContact = async (contact: InvitableContact) => {
  const emailUsage = await fetchEmailUsage(contact.email, contact.defaultOrganization);
  const result = determineContactStatus({
    sendInvitation: true,
    emailUsage: {
      hasAuthUser: emailUsage.hasAuthUser,
      uid: emailUsage.uid,
      existingContacts:
        emailUsage.existingContacts?.map((contact) => ({
          id: contact.id,
          status: contact.status,
          invitationId: contact.invitationId,
          uid: contact.uid,
        })) ?? [],
    },
    currentContact: {
      status: contact.status,
      invitationId: contact.invitationId ?? null,
    },
  });

  const status = result?.status ?? contact.status;
  const invitationId = result && 'invitationId' in result ? result.invitationId : null;
  const uid = result && 'uid' in result ? result.uid : null;

  const data = {
    id: contact.id,
    status,
    invitationType:
      contact.invitationType ??
      (contact.type === ContactType.FAMILY ? INVITATION_TYPES.parent : INVITATION_TYPES.staff),
    invitationNotes: contact.invitationNotes ?? null,
    // The server only re-invites a contact already at inviting when this changes, so plain edits never email.
    inviteRequestedAt: firestore.FieldValue.serverTimestamp(),
    ...(invitationId ? { invitationId } : {}),
    ...(uid ? { uid } : {}),
  };
  // i assume this is an opportunistic update of the local copy of contact data
  contact.status = status;
  return saveContact(contact.defaultOrganization, data);
};

export const uninviteContact = async (contact) => {
  const data = {
    id: contact.id,
    status: ContactStatus.new,
    invitationId: firestore.FieldValue.delete(),
    // Same write as the status change, so no in-between save keeps the old uid's login on.
    ...(contact.uid ? { verifiedAt: null } : {}),
  };
  contact.status = ContactStatus.new;
  return saveContact(contact.defaultOrganization, data);
};

export const fetchContacts = async (organizationId, conditions: QueryConditionType[] = []) => {
  return getDocumentsAsArray({
    path: paths.contacts(organizationId),
    conditions,
    orderBy: [{ field: 'firstName', direction: 'asc' }],
  });
};

/**
 * Look up Contacts by (lowercased) email — ALL matches, because historical
 * duplicates exist and callers matching children must search every one.
 * Deliberately NOT built on `fetchContacts`: its baked-in `orderBy firstName`
 * turns any filtered query into a composite-index requirement (`email == X
 * orderBy firstName` needs an index Firestore doesn't have, and the query
 * throws). An equality filter with no orderBy runs on the automatic
 * single-field index.
 */
export const fetchContactsByEmail = async (
  organizationId: string,
  email: string
): Promise<Array<{ id: string } & Record<string, any>>> => {
  const snapshot = await createQuerySnapshot(paths.contacts(organizationId), [
    { field: 'email', operation: '==', value: email.trim().toLowerCase() },
  ]);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};
